import { useEffect, useMemo, useState } from "react";
import type { UiCommandBusV2, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/foundation/primitives/avatar";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { cn } from "@/ui/foundation/lib/utils";
import { CircleXIcon } from "lucide-react";
import {
    TEAM_SELECTION_LIST_CARD_CLASS,
    TEAM_SELECTION_PITCH_CARD_CLASS,
    avatarDataUri,
    formatFriendlyDate,
    initials,
    renderCompactPips,
    roleLane,
    setCardDragPreview,
    shortSelectionName,
} from "@/ui/components/manager/manager-ui-helpers";

interface UseIntroOverlayStateResult {
    introFormationDraft: string;
    introLaneSlots: {
        gk: UiSnapshotV2["routes"]["intro"]["selection"]["xiSlots"];
        def: UiSnapshotV2["routes"]["intro"]["selection"]["xiSlots"];
        mid: UiSnapshotV2["routes"]["intro"]["selection"]["xiSlots"];
        att: UiSnapshotV2["routes"]["intro"]["selection"]["xiSlots"];
    };
    introSwapSource: { group: "xi" | "bench" | "out"; index: number } | null;
    introDragSource: { group: "xi" | "bench" | "out"; index: number } | null;
    introDragTarget: { group: "xi" | "bench" | "out"; index: number } | null;
    setIntroFormationDraft(value: string): void;
    setIntroSwapSource(value: { group: "xi" | "bench" | "out"; index: number } | null): void;
    setIntroDragSource(value: { group: "xi" | "bench" | "out"; index: number } | null): void;
    setIntroDragTarget(value: { group: "xi" | "bench" | "out"; index: number } | null): void;
    runIntroSwap(
        commands: UiCommandBusV2,
        source: { group: "xi" | "bench" | "out"; index: number },
        target: { group: "xi" | "bench" | "out"; index: number }
    ): void;
}

export function useIntroOverlayState(snapshot: UiSnapshotV2): UseIntroOverlayStateResult {
    const [introFormationDraft, setIntroFormationDraft] = useState<string>("4-4-2");
    const [introSwapSource, setIntroSwapSource] = useState<{ group: "xi" | "bench" | "out"; index: number } | null>(null);
    const [introDragSource, setIntroDragSource] = useState<{ group: "xi" | "bench" | "out"; index: number } | null>(null);
    const [introDragTarget, setIntroDragTarget] = useState<{ group: "xi" | "bench" | "out"; index: number } | null>(null);

    useEffect(() => {
        if (snapshot.routes.intro.selection.formationName) {
            setIntroFormationDraft(snapshot.routes.intro.selection.formationName);
        }
    }, [snapshot.routes.intro.selection.formationName]);

    useEffect(() => {
        setIntroSwapSource(null);
        setIntroDragSource(null);
        setIntroDragTarget(null);
    }, [snapshot.activeOverlayRoute]);

    const introLaneSlots = useMemo(() => {
        const lanes = {
            gk: [] as typeof snapshot.routes.intro.selection.xiSlots,
            def: [] as typeof snapshot.routes.intro.selection.xiSlots,
            mid: [] as typeof snapshot.routes.intro.selection.xiSlots,
            att: [] as typeof snapshot.routes.intro.selection.xiSlots,
        };
        for (const slot of snapshot.routes.intro.selection.xiSlots) {
            const lane = roleLane(slot.role);
            if (lane === "gk" || lane === "def" || lane === "mid" || lane === "att") {
                lanes[lane].push(slot);
            } else {
                lanes.mid.push(slot);
            }
        }
        return lanes;
    }, [snapshot.routes.intro.selection.xiSlots]);

    return {
        introFormationDraft,
        introLaneSlots,
        introSwapSource,
        introDragSource,
        introDragTarget,
        setIntroFormationDraft,
        setIntroSwapSource,
        setIntroDragSource,
        setIntroDragTarget,
        runIntroSwap: (commands, source, target) => {
            if (source.group === target.group && source.index === target.index) {
                return;
            }
            void commands.introSwapSlots(source, target);
            setIntroSwapSource(null);
            setIntroDragSource(null);
            setIntroDragTarget(null);
        },
    };
}

interface IntroSelectionOverlayProps {
    snapshot: UiSnapshotV2;
    commands: UiCommandBusV2;
    teamModalOpen: boolean;
    onCloseTeamModal(): void;
    state: UseIntroOverlayStateResult;
}

function availabilityReasonLabel(availability: string): string {
    const normalized = String(availability ?? "").toLowerCase();
    if (!normalized || normalized === "available" || normalized === "fit") {
        return "";
    }
    if (normalized === "injured") {
        return "Unavailable: injured";
    }
    if (normalized === "suspended") {
        return "Unavailable: suspended";
    }
    return `Unavailable: ${normalized.replace(/_/g, " ")}`;
}

function SelectionAvailabilityIndicator({ availability }: { availability: string }) {
    const reason = availabilityReasonLabel(availability);
    if (!reason) {
        return null;
    }
    return (
        <span
            className="absolute right-1 top-1 z-10 inline-flex items-center justify-center rounded-full bg-background/90 p-0.5"
            title={reason}
            aria-label={reason}
        >
            <CircleXIcon className="size-3.5 text-red-600" />
            <span className="sr-only">{reason}</span>
        </span>
    );
}

export function IntroSelectionOverlay({ snapshot, commands, teamModalOpen, onCloseTeamModal, state }: IntroSelectionOverlayProps) {
    const overlayRoute = snapshot.activeOverlayRoute;
    const showMatchdayIntroModal = overlayRoute === "/manager/intro" && snapshot.routes.intro.visible;
    const showTeamModal = teamModalOpen || showMatchdayIntroModal;

    if (!showTeamModal) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <Card className="relative flex h-[92dvh] w-full max-w-310 flex-col overflow-visible">
                <Button
                    size="icon"
                    variant="secondary"
                    className="absolute right-4 top-0 z-10 h-9 w-9 translate-y-[-45%] rounded-full border shadow"
                    onClick={() => {
                        if (showMatchdayIntroModal) {
                            void commands.introBack();
                        } else {
                            onCloseTeamModal();
                        }
                    }}
                    aria-label="Close team selection"
                >
                    <CircleXIcon className="size-4" />
                </Button>
                <CardHeader>
                    <CardDescription>{snapshot.routes.intro.competition}</CardDescription>
                    <CardTitle>{snapshot.routes.intro.fixture}</CardTitle>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
                    <div className="grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
                        <div>Date: {formatFriendlyDate(snapshot.routes.intro.date)}</div>
                        <div>Location: {snapshot.routes.intro.location}</div>
                    </div>
                    <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[0.8fr_2.8fr_0.8fr]">
                        <Card size="sm" className="min-h-0">
                            <CardHeader><CardTitle className="text-sm">Bench</CardTitle></CardHeader>
                            <CardContent className="grid max-h-full content-start justify-center gap-2 overflow-y-auto px-3 py-2">
                                {snapshot.routes.intro.selection.benchSlots.map((slot) => (
                                    <button
                                        type="button"
                                        key={`intro-bench-${slot.slotIndex}`}
                                        className={cn(
                                            `relative flex items-center gap-2 ${TEAM_SELECTION_LIST_CARD_CLASS}`,
                                            state.introDragSource?.group === "bench" && state.introDragSource.index === slot.slotIndex ? "opacity-35" : "",
                                            state.introDragTarget?.group === "bench" && state.introDragTarget.index === slot.slotIndex ? "ring-2 ring-primary/50" : "",
                                            state.introSwapSource?.group === "bench" && state.introSwapSource.index === slot.slotIndex ? "border-primary bg-primary/10" : "hover:bg-muted/50"
                                        )}
                                        draggable
                                        onDragStart={(event) => {
                                            const source = { group: "bench" as const, index: slot.slotIndex };
                                            state.setIntroDragSource(source);
                                            state.setIntroDragTarget(null);
                                            event.dataTransfer.effectAllowed = "move";
                                            event.dataTransfer.setData("text/plain", `bench:${slot.slotIndex}`);
                                            setCardDragPreview(event);
                                        }}
                                        onDragEnd={() => {
                                            state.setIntroDragSource(null);
                                            state.setIntroDragTarget(null);
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            if (state.introDragSource) {
                                                event.dataTransfer.dropEffect = "move";
                                                state.setIntroDragTarget({ group: "bench", index: slot.slotIndex });
                                            }
                                        }}
                                        onDrop={(event) => {
                                            event.preventDefault();
                                            if (!state.introDragSource) {
                                                return;
                                            }
                                            state.runIntroSwap(commands, state.introDragSource, { group: "bench", index: slot.slotIndex });
                                        }}
                                        onClick={() => {
                                            const target = { group: "bench" as const, index: slot.slotIndex };
                                            if (!state.introSwapSource) { state.setIntroSwapSource(target); return; }
                                            if (state.introSwapSource.group === target.group && state.introSwapSource.index === target.index) { state.setIntroSwapSource(null); return; }
                                            state.runIntroSwap(commands, state.introSwapSource, target);
                                        }}
                                    >
                                        <SelectionAvailabilityIndicator availability={slot.availability} />
                                        <Avatar className="size-8"><AvatarImage alt={slot.playerName} src={avatarDataUri(slot.avatarSvg)} draggable={false} /><AvatarFallback className="text-[10px]">{initials(slot.playerName)}</AvatarFallback></Avatar>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium leading-tight">{shortSelectionName(slot.playerName)}</div>
                                            <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                                                <span>{slot.playerRole}</span>
                                                {renderCompactPips(slot.rating)}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </CardContent>
                        </Card>
                        <Card size="sm" className="min-h-0">
                            <CardHeader>
                                <CardTitle className="text-sm">Starting XI</CardTitle>
                                <CardDescription className="flex items-center gap-2">
                                    <span>Formation</span>
                                    <select className="h-7 rounded-md border bg-background px-2 text-xs" value={state.introFormationDraft} onChange={(event) => state.setIntroFormationDraft(event.target.value)}>
                                        {snapshot.routes.intro.selection.formationOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                                    </select>
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => void commands.introSetFormation(state.introFormationDraft)}>Apply</Button>
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="min-h-0">
                                <div className="relative h-full min-h-110 overflow-hidden rounded-lg border border-white/40 bg-emerald-700/95">
                                    <div className="pointer-events-none absolute inset-0">
                                        <div className="absolute inset-2 rounded-md border border-white/80" />
                                        <div className="absolute top-1/2 left-2 right-2 h-px -translate-y-1/2 bg-white/80" />
                                        <div className="absolute left-1/2 top-1/2 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/80" />
                                        <div className="absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
                                        <div className="absolute left-1/2 top-2 h-24 w-44 -translate-x-1/2 rounded-b-md border-x border-b border-white/80" />
                                        <div className="absolute left-1/2 bottom-2 h-24 w-44 -translate-x-1/2 rounded-t-md border-x border-t border-white/80" />
                                        <div className="absolute left-1/2 top-2 h-11 w-20 -translate-x-1/2 rounded-b-sm border-x border-b border-white/80" />
                                        <div className="absolute left-1/2 bottom-2 h-11 w-20 -translate-x-1/2 rounded-t-sm border-x border-t border-white/80" />
                                        <div className="absolute left-1/2 top-[4.5rem] size-1.5 -translate-x-1/2 rounded-full bg-white/90" />
                                        <div className="absolute left-1/2 bottom-[4.5rem] size-1.5 -translate-x-1/2 rounded-full bg-white/90" />
                                        <div className="absolute left-1/2 top-[6.5rem] h-5 w-24 -translate-x-1/2 overflow-hidden">
                                            <div className="absolute left-0 -top-[4.75rem] h-24 w-24 rounded-full border border-white/80" />
                                        </div>
                                        <div className="absolute left-1/2 bottom-[6.5rem] h-5 w-24 -translate-x-1/2 overflow-hidden">
                                            <div className="absolute left-0 -bottom-[4.75rem] h-24 w-24 rounded-full border border-white/80" />
                                        </div>
                                        <div className="absolute left-2 top-2 h-4 w-4 overflow-hidden">
                                            <div className="absolute -left-2 -top-2 h-6 w-6 rounded-full border border-white/80" />
                                        </div>
                                        <div className="absolute right-2 top-2 h-4 w-4 overflow-hidden">
                                            <div className="absolute -right-2 -top-2 h-6 w-6 rounded-full border border-white/80" />
                                        </div>
                                        <div className="absolute left-2 bottom-2 h-4 w-4 overflow-hidden">
                                            <div className="absolute -bottom-2 -left-2 h-6 w-6 rounded-full border border-white/80" />
                                        </div>
                                        <div className="absolute bottom-2 right-2 h-4 w-4 overflow-hidden">
                                            <div className="absolute -bottom-2 -right-2 h-6 w-6 rounded-full border border-white/80" />
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 grid grid-rows-[0.16fr_0.26fr_0.30fr_0.28fr] gap-2 p-3">
                                        {[state.introLaneSlots.att, state.introLaneSlots.mid, state.introLaneSlots.def, state.introLaneSlots.gk].map((lane, laneIndex) => (
                                            <div
                                                key={`lane-${laneIndex}`}
                                                className={cn(
                                                    "flex items-center justify-center gap-2 overflow-hidden px-1",
                                                    laneIndex === 1 ? "pt-4" : "",
                                                    laneIndex === 2 ? "pt-6" : ""
                                                )}
                                            >
                                                {lane.map((slot) => (
                                                    <button
                                                        type="button"
                                                        key={`intro-xi-${slot.slotIndex}`}
                                                        className={cn(
                                                            `relative ${TEAM_SELECTION_PITCH_CARD_CLASS}`,
                                                            state.introDragSource?.group === "xi" && state.introDragSource.index === slot.slotIndex ? "opacity-35" : "",
                                                            state.introDragTarget?.group === "xi" && state.introDragTarget.index === slot.slotIndex ? "ring-2 ring-primary/50" : "",
                                                            state.introSwapSource?.group === "xi" && state.introSwapSource.index === slot.slotIndex ? "border-primary ring-2 ring-primary/50" : "hover:bg-background"
                                                        )}
                                                        draggable
                                                        onDragStart={(event) => {
                                                            const source = { group: "xi" as const, index: slot.slotIndex };
                                                            state.setIntroDragSource(source);
                                                            state.setIntroDragTarget(null);
                                                            event.dataTransfer.effectAllowed = "move";
                                                            event.dataTransfer.setData("text/plain", `xi:${slot.slotIndex}`);
                                                            setCardDragPreview(event);
                                                        }}
                                                        onDragEnd={() => {
                                                            state.setIntroDragSource(null);
                                                            state.setIntroDragTarget(null);
                                                        }}
                                                        onDragOver={(event) => {
                                                            event.preventDefault();
                                                            if (state.introDragSource) {
                                                                event.dataTransfer.dropEffect = "move";
                                                                state.setIntroDragTarget({ group: "xi", index: slot.slotIndex });
                                                            }
                                                        }}
                                                        onDrop={(event) => {
                                                            event.preventDefault();
                                                            if (!state.introDragSource) {
                                                                return;
                                                            }
                                                            state.runIntroSwap(commands, state.introDragSource, { group: "xi", index: slot.slotIndex });
                                                        }}
                                                        onClick={() => {
                                                            const target = { group: "xi" as const, index: slot.slotIndex };
                                                            if (!state.introSwapSource) { state.setIntroSwapSource(target); return; }
                                                            if (state.introSwapSource.group === target.group && state.introSwapSource.index === target.index) { state.setIntroSwapSource(null); return; }
                                                            state.runIntroSwap(commands, state.introSwapSource, target);
                                                        }}
                                                    >
                                                        <SelectionAvailabilityIndicator availability={slot.availability} />
                                                        <div className="flex items-center gap-2">
                                                            <Avatar className="size-8">
                                                                <AvatarImage alt={slot.playerName} src={avatarDataUri(slot.avatarSvg)} draggable={false} />
                                                                <AvatarFallback className="text-[10px]">{initials(slot.playerName)}</AvatarFallback>
                                                            </Avatar>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="truncate text-sm font-semibold leading-tight">{shortSelectionName(slot.playerName)}</div>
                                                                <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                                                                    <span>{slot.playerRole}</span>
                                                                    {renderCompactPips(slot.rating)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card size="sm" className="min-h-0">
                            <CardHeader><CardTitle className="text-sm">Out of Squad</CardTitle></CardHeader>
                            <CardContent className="grid max-h-full content-start justify-center gap-2 overflow-y-auto px-3 py-2">
                                {snapshot.routes.intro.selection.outSlots.map((slot) => (
                                    <button
                                        type="button"
                                        key={`intro-out-${slot.slotIndex}`}
                                        className={cn(
                                            `relative flex items-center gap-2 ${TEAM_SELECTION_LIST_CARD_CLASS}`,
                                            state.introDragSource?.group === "out" && state.introDragSource.index === slot.slotIndex ? "opacity-35" : "",
                                            state.introDragTarget?.group === "out" && state.introDragTarget.index === slot.slotIndex ? "ring-2 ring-primary/50" : "",
                                            state.introSwapSource?.group === "out" && state.introSwapSource.index === slot.slotIndex ? "border-primary bg-primary/10" : "hover:bg-muted/50"
                                        )}
                                        draggable
                                        onDragStart={(event) => {
                                            const source = { group: "out" as const, index: slot.slotIndex };
                                            state.setIntroDragSource(source);
                                            state.setIntroDragTarget(null);
                                            event.dataTransfer.effectAllowed = "move";
                                            event.dataTransfer.setData("text/plain", `out:${slot.slotIndex}`);
                                            setCardDragPreview(event);
                                        }}
                                        onDragEnd={() => {
                                            state.setIntroDragSource(null);
                                            state.setIntroDragTarget(null);
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            if (state.introDragSource) {
                                                event.dataTransfer.dropEffect = "move";
                                                state.setIntroDragTarget({ group: "out", index: slot.slotIndex });
                                            }
                                        }}
                                        onDrop={(event) => {
                                            event.preventDefault();
                                            if (!state.introDragSource) {
                                                return;
                                            }
                                            state.runIntroSwap(commands, state.introDragSource, { group: "out", index: slot.slotIndex });
                                        }}
                                        onClick={() => {
                                            const target = { group: "out" as const, index: slot.slotIndex };
                                            if (!state.introSwapSource) { state.setIntroSwapSource(target); return; }
                                            if (state.introSwapSource.group === target.group && state.introSwapSource.index === target.index) { state.setIntroSwapSource(null); return; }
                                            state.runIntroSwap(commands, state.introSwapSource, target);
                                        }}
                                    >
                                        <SelectionAvailabilityIndicator availability={slot.availability} />
                                        <Avatar className="size-8"><AvatarImage alt={slot.playerName} src={avatarDataUri(slot.avatarSvg)} draggable={false} /><AvatarFallback className="text-[10px]">{initials(slot.playerName)}</AvatarFallback></Avatar>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium leading-tight">{shortSelectionName(slot.playerName)}</div>
                                            <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                                                <span>{slot.playerRole}</span>
                                                {renderCompactPips(slot.rating)}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                    <div className="flex items-end justify-between gap-3">
                        <div className={cn("max-w-[50%] rounded-md border px-3 py-2 text-sm", snapshot.routes.intro.selection.validation.ok ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5")}>
                            {snapshot.routes.intro.selection.validation.ok
                                ? "Selection valid: ready for match."
                                : `Selection invalid: ${(snapshot.routes.intro.selection.validation.errors ?? []).join(" ")}`}
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="outline" onClick={() => void commands.introAuto()}>Auto Select</Button>
                            {showMatchdayIntroModal ? (
                                <>
                                    <Button onClick={() => void commands.introStart()} disabled={!snapshot.routes.intro.selection.validation.ok}>Start Match</Button>
                                    <Button variant="secondary" onClick={() => void commands.introSkip()} disabled={!snapshot.routes.intro.selection.validation.ok}>Show Result</Button>
                                </>
                            ) : null}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
